import { Input } from "antd";
import { useMemo } from "react";
import { OrganizationTreeSelect } from "@/modules/organization-cross-access/components/OrganizationTreeSelect";
import { EmptyState } from "@/shared/components/EmptyState";
import { PageHeader } from "@/shared/components/PageHeader";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlParams } from "@/shared/hooks/useUrlState";
import { LayerToggle } from "../components/LayerToggle";
import { MonitoringMap } from "../components/MonitoringMap";
import type { MonitoringLayer } from "../types";

const DEFAULT_LAYERS: MonitoringLayer[] = ["territory-brigade", "equipment", "staff"];

// Sentinel for "all layers off" — an empty param value would be dropped from the URL.
const NO_LAYERS = "none";

export function MonitoringPage() {
	// Selected org and active map layers live in the URL so back/forward restores them.
	const { searchParams, setParams } = useUrlParams();
	const orgId = searchParams.get("org") ?? "";
	const layersParam = searchParams.get("layers");
	const search = searchParams.get("q") ?? "";

	const activeLayers = useMemo<MonitoringLayer[]>(() => {
		// Absent param = default layer set.
		if (layersParam === null) {
			return DEFAULT_LAYERS;
		}
		if (layersParam === NO_LAYERS) {
			return [];
		}
		return layersParam.split(",") as MonitoringLayer[];
	}, [layersParam]);

	const handleLayersChange = (layers: MonitoringLayer[]) => {
		const isDefault =
			layers.length === DEFAULT_LAYERS.length && DEFAULT_LAYERS.every((l) => layers.includes(l));
		setParams({
			layers: isDefault ? null : layers.length > 0 ? layers.join(",") : NO_LAYERS,
		});
	};

	return (
		<div>
			<PageHeader title="Monitoring" />

			<TableToolbar
				filters={
					<>
						<div className="min-w-[260px] max-w-[360px] flex-1">
							<OrganizationTreeSelect
								value={orgId}
								onChange={(value) => setParams({ org: value || null })}
							/>
						</div>
						<Input.Search
							// Remount when the URL value changes so back/forward restores the text
							key={search}
							placeholder="Qidirish (xodim, texnika, hudud, ekin)"
							allowClear
							style={{ width: 260 }}
							defaultValue={search}
							onSearch={(v) => setParams({ q: v || null })}
						/>
					</>
				}
				actions={<LayerToggle value={activeLayers} onChange={handleLayersChange} />}
			/>

			{orgId ? (
				<MonitoringMap organizationId={orgId} activeLayers={activeLayers} search={search} />
			) : (
				<EmptyState description="Monitoring uchun tashkilotni tanlang" />
			)}
		</div>
	);
}
